import React, { useMemo, useState } from 'react';
import { Radio, TimePicker, Checkbox, Modal } from 'antd';
import moment from 'moment';

import { SectionCollapse, SizeFittingTable } from '@/src/components/Antd';
import GlobalTooltip, {
  NoWrapContainer,
} from '@/src/components/GlobalTooltip/GlobalTooltip';
import RangePicker, { DateRange } from '@/src/components/RangePicker';
import { ProductItem } from '@/src/types/common/product';
import { handleOnGetCurrentSCTimezoneUTC, i18nGet } from '@/src/tools/tools';
import { useI18n } from 'src/hooks/useRouterChange';

import styles from './style.module.less';
import { Icon } from '@/src/tools/icon';

// 类型定义
export type ProductScheduleType =
  | 'AlwaysOn'
  | 'DateRange'
  | 'RecurringSchedule';

export interface RecurringTimeItem {
  name:
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';
  beginTime: string;
  endTime: string;
  checked: boolean;
}

export interface ProductSchedule {
  productUuid: string;
  productScheduleType: ProductScheduleType;
  dateRangeBeginDate: string | null;
  dateRangeBeginTime: string | null;
  dateRangeEndDate: string | null;
  dateRangeEndTime: string | null;
  recurringScheduleTimeItems: RecurringTimeItem[];
}

interface Props {
  products: Array<ProductItem>;
  productSchedules: ProductSchedule[];
  disabled?: boolean;
  onChange: (schedules: ProductSchedule[]) => void;
}

const WEEKDAYS: RecurringTimeItem['name'][] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const WEEKDAY_I18N_KEYS: Record<string, string> = {
  MONDAY: 'general.monday',
  TUESDAY: 'general.tuesday',
  WEDNESDAY: 'general.wednesday',
  THURSDAY: 'general.thursday',
  FRIDAY: 'general.friday',
  SATURDAY: 'general.saturday',
  SUNDAY: 'general.sunday',
};

const TYPE_I18N_KEYS: Record<ProductScheduleType, string> = {
  AlwaysOn: 'salesChannelProductDetail.index.scheduleAlwaysOn',
  DateRange: 'salesChannelProductDetail.index.scheduleDateRange',
  RecurringSchedule: 'salesChannelProductDetail.index.scheduleRecurring',
};

// 默认循环规则
const getDefaultRecurringItems = (): RecurringTimeItem[] => {
  return WEEKDAYS.map((name) => ({
    name,
    beginTime: '09:00:00',
    endTime: '18:00:59',
    checked: false,
  }));
};

// 初始化 schedule
const getDefaultSchedule = (productUuid: string): ProductSchedule => ({
  productUuid,
  productScheduleType: 'AlwaysOn',
  dateRangeBeginDate: null,
  dateRangeBeginTime: null,
  dateRangeEndDate: null,
  dateRangeEndTime: null,
  recurringScheduleTimeItems: getDefaultRecurringItems(),
});

function AvailabilitySchedule({
  products,
  productSchedules,
  disabled,
  onChange,
}: Props) {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  // Modal 状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(
    null,
  );
  const [tempSchedule, setTempSchedule] = useState<ProductSchedule | null>(
    null,
  );
  const [dateRangeError, setDateRangeError] = useState<{
    hasError: boolean;
    errorMsg: string;
  }>({ hasError: false, errorMsg: '' });
  const [recurringError, setRecurringError] = useState<{
    hasError: boolean;
    errorMsg: string;
  }>({ hasError: false, errorMsg: '' });

  // 为每个产品获取或创建 schedule
  const schedulesMap = useMemo(() => {
    const map = new Map<string, ProductSchedule>();
    productSchedules.forEach((s) => map.set(s.productUuid, s));
    return map;
  }, [productSchedules]);

  const getSchedule = (productUuid: string): ProductSchedule => {
    return schedulesMap.get(productUuid) || getDefaultSchedule(productUuid);
  };

  // 打开 Modal
  const handleOpenModal = (product: ProductItem) => {
    setEditingProduct(product);
    setTempSchedule({ ...getSchedule(product.uuid) });
    setModalVisible(true);
  };

  // 关闭 Modal
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingProduct(null);
    setTempSchedule(null);
    setDateRangeError({ hasError: false, errorMsg: '' });
    setRecurringError({ hasError: false, errorMsg: '' });
  };

  // 验证日期范围
  const validateDateRange = (): boolean => {
    if (!tempSchedule) return false;

    // 只有当选择了 DateRange 类型时才需要验证
    if (tempSchedule.productScheduleType === 'DateRange') {
      const { dateRangeBeginDate, dateRangeBeginTime } = tempSchedule;

      // Start Date 是必填的
      if (!dateRangeBeginDate || !dateRangeBeginTime) {
        setDateRangeError({
          hasError: true,
          errorMsg: getI18n(
            'salesChannelProductDetail.index.scheduleStartDateRequired',
          ),
        });
        return false;
      }
    }

    setDateRangeError({ hasError: false, errorMsg: '' });
    return true;
  };

  // 验证循环规则
  const validateRecurring = (): boolean => {
    if (!tempSchedule) return false;

    if (tempSchedule.productScheduleType !== 'RecurringSchedule') {
      setRecurringError({ hasError: false, errorMsg: '' });
      return true;
    }

    const items = tempSchedule.recurringScheduleTimeItems || [];
    const checkedItems = items.filter((item) => item.checked);

    if (checkedItems.length === 0) {
      setRecurringError({
        hasError: true,
        errorMsg: getI18n(
          'salesChannelProductDetail.index.scheduleAtLeastOneDay',
        ),
      });
      return false;
    }

    setRecurringError({ hasError: false, errorMsg: '' });
    return true;
  };

  // 确认保存
  const handleConfirm = () => {
    if (!tempSchedule || !editingProduct) return;

    // 验证日期范围
    if (!validateDateRange()) {
      return;
    }

    // 验证循环规则
    if (!validateRecurring()) {
      return;
    }

    const newSchedules = productSchedules.filter(
      (s) => s.productUuid !== editingProduct.uuid,
    );
    newSchedules.push(tempSchedule);
    onChange(newSchedules);
    handleCloseModal();
  };

  // Modal 内的更新方法
  const handleTypeChange = (type: ProductScheduleType) => {
    if (!tempSchedule) return;
    const updates: Partial<ProductSchedule> = { productScheduleType: type };

    if (
      type === 'RecurringSchedule' &&
      !tempSchedule.recurringScheduleTimeItems?.length
    ) {
      updates.recurringScheduleTimeItems = getDefaultRecurringItems();
    }

    setTempSchedule({ ...tempSchedule, ...updates });
    // 清除错误状态
    setDateRangeError({ hasError: false, errorMsg: '' });
    setRecurringError({ hasError: false, errorMsg: '' });
  };

  const handleDateRangeChange = (value: DateRange) => {
    if (!tempSchedule) return;
    setTempSchedule({
      ...tempSchedule,
      dateRangeBeginDate: value?.beginDate || null,
      dateRangeBeginTime: value?.beginTime || null,
      dateRangeEndDate: value?.endDate || null,
      dateRangeEndTime: value?.endTime || null,
    });
    // 清除错误状态
    setDateRangeError({ hasError: false, errorMsg: '' });
  };

  const handleWeekdayToggle = (dayName: RecurringTimeItem['name']) => {
    if (!tempSchedule) return;
    const items = [
      ...(tempSchedule.recurringScheduleTimeItems ||
        getDefaultRecurringItems()),
    ];
    const index = items.findIndex((item) => item.name === dayName);
    if (index >= 0) {
      items[index] = { ...items[index], checked: !items[index].checked };
      setTempSchedule({ ...tempSchedule, recurringScheduleTimeItems: items });
      setRecurringError({ hasError: false, errorMsg: '' });
    }
  };

  const handleTimeRangeChange = (
    dayName: RecurringTimeItem['name'],
    times: string[],
  ) => {
    if (!tempSchedule || !times || times.length !== 2) return;
    const items = [
      ...(tempSchedule.recurringScheduleTimeItems ||
        getDefaultRecurringItems()),
    ];
    const index = items.findIndex((item) => item.name === dayName);
    if (index >= 0) {
      items[index] = {
        ...items[index],
        beginTime: times[0],
        endTime: times[1],
      };
      setTempSchedule({ ...tempSchedule, recurringScheduleTimeItems: items });
      setRecurringError({ hasError: false, errorMsg: '' });
    }
  };

  // 获取 Available Time 显示文本
  const getAvailableTimeDisplay = (schedule: ProductSchedule): string => {
    return getI18n(
      TYPE_I18N_KEYS[schedule.productScheduleType] ||
        'salesChannelProductDetail.index.scheduleAlwaysOn',
    );
  };

  // 表格列定义
  const columns = [
    {
      width: 56,
      default: true,
      title: '',
      key: 'index',
      childGenerate: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: getI18n('salesChannelProductDetail.index.scheduleProduct'),
      default: true,
      minWidth: 400,
      key: 'product',
      childGenerate: (_: any, record: ProductItem) => {
        return (
          <div className={styles.products}>
            {record.thumbnailUrl && (
              <div
                className={styles.img}
                style={{
                  backgroundImage: `url('${record.thumbnailUrl}')`,
                  marginRight: 8,
                }}
              />
            )}
            <NoWrapContainer>
              <GlobalTooltip title={record.name || '-'}>
                {record.name || '-'}
              </GlobalTooltip>
            </NoWrapContainer>
          </div>
        );
      },
    },
    {
      title: (
        <>
          <span>
            {!disabled && <Icon type={'iconedit'} className={styles.icon} />}
            {getI18n('salesChannelProductDetail.index.availableTime')}
          </span>
        </>
      ),
      align: 'center',
      minWidth: 180,
      default: true,
      key: 'availableTime',
      childGenerate: (_: any, record: ProductItem) => {
        const schedule = getSchedule(record.uuid);
        return (
          <div
            className={styles.ahref}
            onClick={() => !disabled && handleOpenModal(record)}>
            {getAvailableTimeDisplay(schedule)}
          </div>
        );
      },
    },
  ];

  const scTimezoneUTC = handleOnGetCurrentSCTimezoneUTC();

  // 渲染 Modal 内容
  const renderModalContent = () => {
    if (!tempSchedule) return null;

    const {
      productScheduleType,
      dateRangeBeginDate,
      dateRangeBeginTime,
      dateRangeEndDate,
      dateRangeEndTime,
      recurringScheduleTimeItems,
    } = tempSchedule;

    return (
      <div className={styles.modalContent}>
        <Radio.Group
          value={productScheduleType}
          onChange={(e) => handleTypeChange(e.target.value)}
          className={styles.radioGroup}>
          <Radio value='AlwaysOn'>
            {getI18n('salesChannelProductDetail.index.scheduleAlwaysOn')}
          </Radio>
          <div className={styles.radioWithInput}>
            <Radio value='DateRange'>
              {getI18n('salesChannelProductDetail.index.scheduleDateRange')}{' '}
              (UTC {scTimezoneUTC})
            </Radio>
            {productScheduleType === 'DateRange' && (
              <div className={styles.dateRangeInput}>
                <RangePicker
                  id={`schedule-date-range-${editingProduct?.uuid}`}
                  size='large'
                  hideNoEndTime
                  value={{
                    beginDate: dateRangeBeginDate || '',
                    beginTime: dateRangeBeginTime || '',
                    endDate: dateRangeEndDate || '',
                    endTime: dateRangeEndTime || '',
                  }}
                  onChange={handleDateRangeChange}
                  occupiedRanges={[]}
                  error={dateRangeError}
                />
              </div>
            )}
          </div>
          <div className={styles.radioWithInput}>
            <Radio value='RecurringSchedule'>
              {getI18n('salesChannelProductDetail.index.scheduleRecurring')}{' '}
              (UTC {scTimezoneUTC})
            </Radio>
          </div>
        </Radio.Group>

        {productScheduleType === 'RecurringSchedule' && (
          <div className={styles.recurringSection}>
            {recurringError.hasError && (
              <div style={{ color: '#ff695f', marginBottom: 8 }}>
                {recurringError.errorMsg}
              </div>
            )}
            {(recurringScheduleTimeItems || getDefaultRecurringItems()).map(
              (item) => (
                <div key={item.name} className={styles.weekItem}>
                  <Checkbox
                    checked={item.checked}
                    onChange={() => handleWeekdayToggle(item.name)}
                    className={styles.checkbox}>
                    <span className={styles.checkboxText}>
                      {getI18n(WEEKDAY_I18N_KEYS[item.name])}
                    </span>
                  </Checkbox>

                  <div className={styles.timePickerItem}>
                    <TimePicker.RangePicker
                      value={[
                        moment(item.beginTime, 'HH:mm:ss'),
                        moment(item.endTime, 'HH:mm:ss'),
                      ]}
                      format='HH:mm'
                      onChange={(times) => {
                        if (times) {
                          handleTimeRangeChange(item.name, [
                            (times[0]?.format('HH:mm') || '09:00') + ':00',
                            (times[1]?.format('HH:mm') || '18:00') + ':59',
                          ]);
                        }
                      }}
                      disabled={!item.checked}
                      allowClear={false}
                      className={styles.timePickerInput}
                    />
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (products.length === 0) {
      return (
        <div className={styles.empty}>
          {getI18n('salesChannelProductDetail.index.scheduleNoProducts')}
        </div>
      );
    }

    return (
      <div className={styles.content}>
        <div className={styles.table}>
          <div className={styles.tableTitle}>
            {getI18n('salesChannelProductDetail.index.scheduleDescription')}
          </div>
          <SizeFittingTable
            id='availabilityScheduleList'
            rowKey={'uuid'}
            className={styles.table}
            dataSource={products}
            pagination={false}
            columns={columns}
            scroll={{}}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <SectionCollapse
        open={true}
        id='salesChannelLinkProductAvailabilitySchedule'
        titleStyle={{ height: '64px' }}
        title={
          <div className='collapseTitle'>
            {getI18n('salesChannelProductDetail.index.scheduleTitle')}
          </div>
        }
        content={renderContent()}
        openMargin={16}
      />
      <Modal
        title={getI18n('salesChannelProductDetail.index.scheduleTitle')}
        visible={modalVisible}
        onCancel={handleCloseModal}
        onOk={handleConfirm}
        okText={getI18n('general.confirm')}
        cancelText={getI18n('general.cancel')}
        width={600}
        destroyOnClose>
        {renderModalContent()}
      </Modal>
    </div>
  );
}

export default AvailabilitySchedule;
